import { useCurrentAccount, useResolvedFeatureFlag } from '@alliance-disposal/client';
import { FeatureFlagKeys } from '@alliance-disposal/transport-types';
import { type DispatchTaskType, getEntitledDispatchTaskTypes } from './dispatch-params';

/**
 * Task types the unified `/dispatch` view should serve this hauler. Empty means they belong on the legacy dispatch
 * pages; `null` means the rollout flag hasn't resolved yet.
 *
 * One decision for every entry point — the nav links, the legacy route redirects and the `/dispatch` route guard —
 * so a hauler can't be linked to one dispatch page and redirected to the other.
 *
 * Each surface asks about the task type it is about: the roll-off nav entry and `/ro-schedule/` only move once `ro`
 * is served, portable toilet only once `pt` is. Entitlement is the whole gate beyond the rollout flag —
 * `getEntitledDispatchTaskTypes` already requires `wayste-operations-rolloff` for `ro`, so a hauler without it can
 * never be shown the operations roll-off stack, while a portable-toilet-only hauler is no longer held back by a
 * roll-off flag that says nothing about their dispatch page.
 *
 * Resolved via `useResolvedFeatureFlag` rather than `useFeatureFlagEnabled` because the raw hook reports
 * `undefined` until PostHog reports flags, and App's auth subscription calls `posthog.reset()`/`identify()`/
 * `group()` on every load — so the raw value churns. Callers that decide which page to mount should hold on
 * `null` rather than mounting one and correcting a beat later.
 */
export const useUnifiedDispatchTaskTypes = (): DispatchTaskType[] | null => {
    const { featureFlags } = useCurrentAccount();
    const unifiedDispatchFlag = useResolvedFeatureFlag(FeatureFlagKeys.UNIFIED_DISPATCH_VIEW);

    if (unifiedDispatchFlag === null) return null;

    return unifiedDispatchFlag ? getEntitledDispatchTaskTypes(featureFlags) : [];
};
